import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Menu, Modal, Text, TextInput, Tooltip } from "@mantine/core";
import { DateTime } from "luxon";
import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Autosuggest from "react-autosuggest";
import { connect, useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import {
  addLoading,
  setPaysheetVisibilityStatus,
} from "../../../data/actions/appointmentActions";
import SearchApi from "../../../data/api/searchApi";
import { IState } from "../../../data/reducers/state";
import { SSColors } from "../../../styles/scheme";
import {
  IAppointment,
  IAppointmentDetail,
  IGameAppointmentDetail,
  IOfficialLoading,
} from "../../../types/appointments/Appointment";
import { AppointmentStatus } from "../../../types/appointments/AppointmentStatus";
import { ITimePeriod } from "../../../types/availabilities/Availabilities";
import { BasketballGrade } from "../../../types/common/basketballGrades";
import { RefereeCoachGrade } from "../../../types/common/refereeCoachGrades";
import { StatisticianGrade } from "../../../types/common/statisticianGrade";
import { IGame } from "../../../types/game/Game";
import { BonusType } from "../../../types/payrates/bonusType";
import { Role } from "../../../types/persons/Role";
import { IPerson } from "../../../types/persons/person";
import { IShift } from "../../../types/shifts/Shift";
import { toDateTime } from "../../../util/date+helper";
import AppointmentView from "../../appointments/AppointmentView";
import Checkmark from "../../common/Checkmark";
import { confirm } from "../../common/Confirm";

interface OfficialsSelectorProps {
  name: string;
  role: Role;
  label?: string;
  disabled?: boolean;
  appointmentId?: string | null;
  // pass through for appointment details scope
  sessionId?: string | null;

  showHideAppointmentFromSheetButton?: boolean;

  // Alternative to appointmentId - direct person selection
  person?: string | null;

  additionalActions?: {
    title: string;
    action: Function;
    disbaled?: boolean;
    color?: string;
  }[];

  // handlers
  onChange: (
    position: string,
    person: string,
    hasApprentice?: boolean,
  ) => Promise<any> | void;
  onClear?: (position: string, comment?: string) => Promise<any> | void;

  // Filtering options
  gameId?: string | null;
  shiftId?: string | null;
  timePeriod?: ITimePeriod | null;

  /// Call this function just before the autosuggest window is window is populated
  prefilter?: (person: string) => boolean;
  /// if included, only search from these people, the prefilter is still called
  personIds?: string[] | null;
  /// if included, exclude these people under all circumstances
  excludeIds?: string[] | null;
  /// timePeriod to filter by

  // from state
  shift?: IShift | null;
  game?: IGame | null;
  personsMap: { [id: string]: IPerson };
  appointment: IAppointment<IAppointmentDetail> | null;

  actions: {
    addLoading: (
      appointmentId: string,
      loading: IOfficialLoading,
    ) => Promise<IAppointment<IAppointmentDetail>>;
    setPaysheetVisibilityStatus: (
      appointmentId: string,
      shouldShow: boolean,
    ) => Promise<boolean>;
  };
}

interface OfficialsSelectorState {
  textValue?: string | React.ReactElement;

  buildingIds: boolean;
  cachedSuggestionIds?: string[]; // result after all the prefilters have been called
  cachedMaybes?: string[];

  displaySuggestionIds: string[];
  displayMaybeIds?: string[];
  hasCreatedSuggestionCache: boolean;
  tooltipOpen: boolean;
  isDroppingDown: boolean;
  showLoadingView?: boolean;

  showAppointmentDetails: boolean;
}

class OfficialsSelectorOld extends React.PureComponent<
  OfficialsSelectorProps,
  OfficialsSelectorState
> {
  constructor(props, content) {
    super(props, content);

    let textValue: string | React.ReactElement | undefined;
    if (this.props.appointment?.person) {
      textValue = this.getDisplayForPersonId(
        this.props.appointment?.person,
        false,
      );
    }

    this.state = {
      buildingIds: false,
      textValue: textValue || "",
      displaySuggestionIds: [],
      cachedSuggestionIds: undefined,
      hasCreatedSuggestionCache: false,
      tooltipOpen: false,
      isDroppingDown: false,
      showLoadingView: false,
      showAppointmentDetails: false,
    };

    this.onTextValueChange = this.onTextValueChange.bind(this);
    this.buildSuggestions = this.buildSuggestions.bind(this);
    this.renderSuggestion = this.renderSuggestion.bind(this);
    this.getDisplayForPersonId = this.getDisplayForPersonId.bind(this);
    this.onSuggestionSelected = this.onSuggestionSelected.bind(this);
    this.onSuggestionsClearRequested =
      this.onSuggestionsClearRequested.bind(this);
    this.toggleTooltip = this.toggleTooltip.bind(this);
    this.toggleDropdown = this.toggleDropdown.bind(this);
    this.toggleAddLoading = this.toggleAddLoading.bind(this);
    this.renderInput = this.renderInput.bind(this);
    this.addLoading = this.addLoading.bind(this);
    this.rosterOfficial = this.rosterOfficial.bind(this);
  }

  componentDidUpdate(oldProps: OfficialsSelectorProps) {
    this.checkIfPropsChanged(oldProps, this.props);
  }

  checkIfPropsChanged(
    oldProps: OfficialsSelectorProps,
    newProps: OfficialsSelectorProps,
  ) {
    const oldPerson = oldProps.appointment?.person;
    const newPerson = newProps.appointment?.person;

    // maybe need to work out when to invalidate the suggestions cache

    if (newPerson != oldPerson) {
      return this.setState((state) => ({
        ...state,
        textValue: newPerson
          ? this.getDisplayForPersonId(newPerson, false) || ""
          : "",
      }));
    }
  }

  prefix(r: Role) {
    const def = "an official";
    if (!r) return def;

    switch (r) {
      case Role.referee:
        return "a referee";
      case Role.scoretable:
        return "a scorer";
      case Role.statistician:
        return "a statistician";
      case Role.supervisor:
        return "a supervisor";
      default:
        return def;
    }
  }

  addLoading(loading: BonusType) {
    const apId = this.props.appointmentId;
    if (!apId) return;

    this.props.actions
      .addLoading(apId, {
        reason: loading,
        value: BonusType.defaultBonusForReason(
          loading,
          this.props.appointment?.payment?.base || 0,
        ),
      })
      .then((resp) => {
        console.log(resp);
      })
      .catch((er) => {
        alert(er.message);
        console.error(er);
      });
  }

  toggleTooltip() {
    this.setState((state) => ({ ...state, tooltipOpen: !state.tooltipOpen }));
  }

  toggleDropdown() {
    this.setState((state) => ({
      ...state,
      isDroppingDown: !state.isDroppingDown,
    }));
  }

  toggleAddLoading(appointmentId?: string | null) {
    this.setState((state) => ({
      ...state,
      showLoadingView: true,
      showAppointmentDetails: true,
    }));
  }

  render() {
    const person = this.props.appointment?.person;
    const personObj =
      person && this.props.personsMap && this.props.personsMap[person];
    if (!this.props.role) return <p>Loading ...</p>;

    let backgroundColor = "";
    const { appointment } = this.props;
    const appointmentStatus = appointment && appointment.status;

    if (!person) backgroundColor = SSColors.emptyOfficial;
    else if (!appointment) {
      if (this.props.appointmentId) backgroundColor = "red";
    } else if (appointmentStatus == AppointmentStatus.rejected)
      backgroundColor = SSColors.rejectedAppointment;
    // @ts-ignore (if it's a gameAppointment)
    else if (appointment.details.apprentice)
      backgroundColor = SSColors.apprentice;

    const checkSpanId = `confirmStatus_${this.props.appointmentId}`;
    let statusObj: React.ReactElement | null = null;
    let statusText: string | null = null;

    // show tick
    if (appointment && appointmentStatus) {
      if (AppointmentStatus.shouldShowTick(appointmentStatus)) {
        const confirmStatus = appointment.statuses.filter(
          (s) => s.status == AppointmentStatus.confirmed,
        );
        const person = confirmStatus[0]?.person;
        const personsMap = this.props.personsMap;
        const personName = personsMap?.[person]?.preferredName || "Someone";
        let reason = ''
        if (!!confirmStatus[0]?.comment) {
          reason = ` Reason: '${confirmStatus[0].comment}'`;
        }

        statusObj = <Checkmark id={checkSpanId} />;
        statusText = `${personName} confirmed their appointment on ${confirmStatus && toDateTime(confirmStatus[0].date).toLocaleString(DateTime.DATETIME_FULL)}${reason}`;
      } else if (appointmentStatus == AppointmentStatus.rejected) {
        const rejectStatus = appointment.statuses.filter(
          (s) => s.status == AppointmentStatus.rejected,
        );
        statusObj = (
          <span id={checkSpanId} style={{ color: "red" }}>
            {" "}
            &#10005;
          </span>
        );
        statusText = `${personObj && (personObj.preferredName || personObj.firstName)
          } rejected their appointment with the reason: '${rejectStatus[0].comment
          } '`;
      }
    }

    const hasApprentice =
      // @ts-ignore (IGameAppointment)
      (this.props.appointment && this.props.appointment.details.apprentice) ||
      false;

    const isDisabled =
      this.props.disabled ||
      (this.props.appointment &&
        !AppointmentStatus.canRemoveOrChange(this.props.appointment.status)) ||
      false;

    const additionalSideActions: {
      title: string;
      action: Function;
      color?: string;
      disabled?: boolean;
    }[][] = [];
    if (person) {
      additionalSideActions.push([
        {
          title: `Remove ${(personObj && (personObj.preferredName || personObj.firstName)) ||
            this.props.label
            }`,
          action: () => this.props.onClear?.(this.props.name),
          color: "var(--color-attention-red)",
        },
        {
          title: `Withdrawal ${(personObj && (personObj.preferredName || personObj.firstName)) ||
            this.props.label
            } (with replacement)`,
          action: () =>
            this.props.onClear?.(this.props.name, "Withdrawal, with replacement"),
          color: "var(--color-attention-red)",
        },
        {
          title: `Withdrawal ${(personObj && (personObj.preferredName || personObj.firstName)) ||
            this.props.label
            } (no replacement)`,
          action: () =>
            this.props.onClear?.(this.props.name, "Withdrawal, no replacement"),
          color: "var(--color-attention-red)",
        },
      ]);

      const secondSet: { title: string; action: Function }[] = [];
      const thirdSet: { title: string; action: Function }[] = [];

      if (Role.hasRole(this.props.role, Role.referee)) {
        secondSet.push({
          title: `${hasApprentice ? "Remove" : "Add"} apprentice`,
          action: () =>
            this.props.onChange(this.props.name, person, !hasApprentice),
        });
      }
      thirdSet.push({
        title: "Add loading",
        action: () => this.toggleAddLoading(this.props.appointmentId),
      });
      // show these bonus types on thirdSet
      const bonusTypes = [BonusType.solo, BonusType.venueCaptain];
      for (const bonusType of bonusTypes) {
        const hasBonusType = appointment?.payment?.bonuses?.some(
          (b) => b.reason === bonusType,
        );
        if (!hasBonusType) {
          const defaultBonusValue = BonusType.defaultBonusForReason(
            bonusType,
            this.props.appointment?.payment?.base || 0,
          );
          thirdSet.push({
            title: `Add ${BonusType.toLongReason(
              bonusType,
            ).toLowerCase()} loading ($${defaultBonusValue})`,
            action: () => this.addLoading(bonusType),
          });
        }
      }

      if (appointment && this.props.showHideAppointmentFromSheetButton) {
        const shouldShow = appointment?.payment?.showTotal === false;
        const hideShow = shouldShow ? "Show" : "Hide";
        secondSet.push({
          title: `${hideShow} appointment total on paysheet`,
          action: () =>
            this.props.actions.setPaysheetVisibilityStatus(
              appointment._id,
              shouldShow,
            ),
        });
      }

      additionalSideActions.push(secondSet);
      additionalSideActions.push(thirdSet);
    }

    if (this.props.additionalActions) {
      additionalSideActions.push(this.props.additionalActions);
    }

    const rightPadding = additionalSideActions.length > 0 ? 25 : 0;
    const hideAppointmentModal = () =>
      this.setState({ showAppointmentDetails: false });

    const suggestions = [
      ...(this.state.displaySuggestionIds || []).map((pid) => ({
        pid,
        available: true,
      })),
      ...(this.state.displayMaybeIds || []).map((pid) => ({
        pid,
        available: false,
      })),
    ];

    //
    return (
      <div style={{ whiteSpace: "nowrap" }}>
        <div
          style={{
            display: "inline-block",
            width: `calc(100% - ${rightPadding}px)`,
          }}
        >
          <Autosuggest
            id={this.props.name}
            renderInputComponent={this.renderInput}
            suggestions={suggestions}
            onSuggestionsFetchRequested={(event) =>
              this.buildSuggestions(event.value)
            }
            onSuggestionsClearRequested={this.onSuggestionsClearRequested}
            onSuggestionSelected={this.onSuggestionSelected}
            renderSuggestionsContainer={
              OfficialsSelectorOld.renderSuggestionsContainer
            }
            getSuggestionValue={this.getDisplayForPersonId}
            renderSuggestion={this.renderSuggestion}
            focusInputOnSuggestionClick={false}
            shouldRenderSuggestions={(_) => true}
            inputProps={{
              placeholder: `Type ${this.prefix(this.props.role)}'s name...`,
              value: this.state.textValue || "",
              onChange: this.onTextValueChange,
              disabled: isDisabled,
              style: {
                ...{},
                backgroundColor,
                border: "none",
                outline: "none",
                padding: "0px 5px 0px 10px",
                borderBottom: "1px solid var(--mantine-color-gray-4)",
                borderRadius: "1px",
                width: "100%",
                boxShadow: "none",
              },
            }}
            disabled={isDisabled}
          />
          {this.props.label && (
            <Text size="sm" fw={500} style={{ color: "gray", fontSize: "14px" }}>
              <em>
                {this.props.label} {hasApprentice ? "(+Ap)" : ""}
              </em>{" "}
              {statusText && statusObj ? (
                <Tooltip
                  label={statusText}
                  opened={this.state.tooltipOpen}
                  position="right"
                >
                  <span id={checkSpanId} onClick={this.toggleTooltip} style={{ cursor: "pointer" }}>
                    {statusObj}
                  </span>
                </Tooltip>
              ) : (statusObj || "")}
            </Text>
          )}
        </div>
        {additionalSideActions.length > 0 && (
          <div style={{ display: "inline-block", verticalAlign: "top" }}>
            <Menu
              opened={this.state.isDroppingDown}
              onClose={() => this.setState({ isDroppingDown: false })}
              onOpen={() => this.setState({ isDroppingDown: true })}
            >
              <Menu.Target>
                <Button
                  variant="light"
                  color="gray"
                  size="sm"
                  style={{ padding: "5px 4px" }}
                  onClick={this.toggleDropdown}
                >
                  <FontAwesomeIcon icon={faEllipsisV} />
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                {additionalSideActions.map((actions, idx) => (
                  <div key={idx}>
                    {idx !== 0 && <Menu.Divider />}
                    {actions.map((action, idx) => (
                      <Menu.Item
                        disabled={action.disabled}
                        onClick={(e) => action.action(e)}
                        style={{ color: action.color, height: "5vh" }}
                        key={idx}
                      >
                        {action.title}
                      </Menu.Item>
                    ))}
                  </div>
                ))}
                <Menu.Divider />
                <Menu.Item
                  style={{ height: "5vh" }}
                  onClick={() =>
                    this.setState({ showAppointmentDetails: true })
                  }
                >
                  Show details
                </Menu.Item>
                {appointment?.payment && (
                  <Menu.Label>
                    Total: ${Number(appointment.payment.total).toFixed(2)}
                  </Menu.Label>
                )}
              </Menu.Dropdown>
            </Menu>
          </div>
        )}

        {this.state.showAppointmentDetails && (
          <Modal
            opened={this.state.showAppointmentDetails}
            onClose={hideAppointmentModal}
            title="Appointment"
          >
            <AppointmentView
              appointmentId={this.props.appointmentId!}
              defaultTab={this.state.showLoadingView ? "Bonuses" : undefined}
              sessionId={this.props.sessionId}
              disableEdits={false}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
              <Button
                variant="outline"
                color="gray"
                onClick={hideAppointmentModal}
              >
                Close
              </Button>
            </div>
          </Modal>
        )}
      </div>
    );
  }

  onTextValueChange(e, { newValue }) {
    this.setState({ textValue: newValue });
  }

  rosterOfficial(pid) {
    let value: any | null = null;

    if (this.props.role == Role.referee) {
      // @ts-ignore (gameAppointment)
      const hasApprentice =
        this.props.appointment && (this.props.appointment.details as IGameAppointmentDetail).apprentice;
      value = this.props.onChange(this.props.name, pid, hasApprentice || false);
    } else {
      value = this.props.onChange(this.props.name, pid);
    }
    if (value && Boolean(value && typeof value.then === "function")) {
      value.catch((er) => this.setState({ textValue: "" }));
    }
  }

  onSuggestionSelected(
    event,
    { suggestion, suggestionValue, suggestionIndex, sectionIndex, method },
  ) {
    const { pid, available } = suggestion;

    if (!available) {
      const name = this.props.personsMap[pid]?.fullName || pid;
      confirm({
        title: `${name} is not available`,
        message: "Do you want to allocate them to the game anyway?",
        confirmText: "Yes",
      }).then((confirmed) => {

        if (confirmed) {
          this.rosterOfficial(pid);
        }
      });
    } else {
      this.rosterOfficial(pid);
    }
  }

  renderSuggestion(person, { query, isHighlighted }) {
    return this.getDisplayForPersonId(person, true);
  }

  onSuggestionsClearRequested() {
    const suggestions = [];
    this.setState((state) => ({
      ...state,
      displaySuggestionIds: suggestions,
      displayMaybeIds: suggestions,
    }));
  }

  getDisplayForPersonId(
    obj: string | { pid: string; available: boolean },
    formatted: boolean,
  ): string | React.ReactElement {
    let personId;
    let isAvailable;
    if (typeof obj === "string") {
      personId = obj;
      isAvailable = true;
    } else {
      // @ts-ignore
      personId = obj.pid;
      // @ts-ignore
      isAvailable = obj.available;
    }

    if (!personId) return "";
    if (!this.props.personsMap) return "";
    const person = this.props.personsMap[personId];
    if (!person) return "";

    let grade: string | React.ReactElement = "";
    if (Role.hasRole(this.props.role, Role.referee)) {
      // grade =
      //   BasketballGrade.toShortString(person?.referee?.grade || 0) || "UG";
      if (formatted) {
        grade =
          BasketballGrade.componentFromPersonWithDecorations(person) || "";
      } else {
        grade = BasketballGrade.stringFromPersonWithDecorations(person) || "";
      }
    } else if (Role.hasRole(this.props.role, Role.refereeCoach)) {
      grade =
        RefereeCoachGrade.toShortString(person?.refereeCoach?.grade || 0) ||
        "UG";
    } else if (Role.hasRole(this.props.role, Role.statistician)) {
      const statGrade = person?.statistician?.grade ?? 0;
      const baseGrade =
        statGrade === 0
          ? "UG"
          : StatisticianGrade.toShortString(statGrade) || "UG";
      grade = person?.statistician?.fibaAccredited
        ? `${baseGrade} • FIBA`
        : baseGrade;
    } else {
      grade = Role.toString(this.props.role);
    }

    if (!isAvailable && formatted) {
      return (
        <p style={{ color: "grey" }}>
          {person.fullName} ({grade}
          )
          <br />
          <em>Not available</em>
        </p>
      );
    }

    if (formatted) {
      return (
        <p>
          {person.fullName} ({grade})
        </p>
      );
    }
    return `${person.fullName} (${grade})`;
  }

  renderInput({ disabled, onBlur, ...other }) {
    const { styles } = other;
    // remove key from other
    const { key, ...otherNoKey } = other;
    return (
      <>
        <TextInput
          disabled={disabled}
          variant="unstyled"
          onBlur={(e) => {
            if (onBlur) onBlur(e);
            this.setState({});
          }}
          {...otherNoKey}
        />
        {this.state.buildingIds && (
          <div>
            <span
              className="loading"
              style={{
                position: "absolute",
                top: ".2rem",
                right: "0.1rem",
                zIndex: 2,
                display: "block",
                textAlign: "center",
                pointerEvents: "none",

              }}
            />
            <div
              style={{ backgroundColor: styles?.backgroundColor || "#FFF" }}
            />
          </div>
        )}
      </>
    );
  }

  static renderSuggestionsContainer(options) {
    const { containerProps, children, key } = options;
    // remove key from containerProps
    const { key: _, ...containerPropsNoKey } = containerProps
    return (
      <div {...containerPropsNoKey}>
        <div
          style={{
            zIndex: 999,
            overflow: "visible",
            position: "absolute",
            backgroundColor: "var(--color-bg-card)",
            border: "1px solid var(--color-divider)",
            borderRadius: "4px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
            color: "var(--color-text-primary)"
          }}
        >
          {children}
          {!children && (
            <p>
              <em>There are no results</em>
            </p>
          )}
        </div>
      </div>
    );
  }

  async buildSuggestions(query) {
    /**
     * This is really where the bulk of everything happens,
     */
    const store = this.props.personsMap;
    this.setState({ textValue: query });

    if (this.state.buildingIds) return null;

    const filter = (initial: string[]) => {
      const startSuggestions: string[] = initial;
      const searchComponents: string[] = query.trim().toLowerCase().split(" ");

      const filterToRuleThemAll = (p: string) => {
        if (!(p in store)) {
          console.log(`Warning: Person '${p}' was not found in the store`);
          return false;
        }
        const person = store[p];

        return searchComponents.every((q) => {
          const l = q.length;
          const preferredMatch =
            !!person.preferredName &&
            person.preferredName.toLowerCase().slice(0, l) === q;
          const firstNameMatch =
            person.firstName.toLowerCase().slice(0, l) === q;
          const lastNameMatch = person.lastName.toLowerCase().slice(0, l) === q;
          const refereeMatch =
            !!person.referee &&
            !!person.referee.id &&
            String(person.referee.id).slice(0, l) === q;
          return (
            preferredMatch || firstNameMatch || lastNameMatch || refereeMatch
          );
        });
      };

      const suggestions = startSuggestions.filter(filterToRuleThemAll);
      return suggestions;
    };

    // whatever this is at the end
    let { cachedSuggestionIds, cachedMaybes } = this.state;
    if (!cachedSuggestionIds || cachedSuggestionIds.length === 0) {
      const result = await this.buildInitialSuggestions();
      cachedSuggestionIds = result.cachedSuggestionIds;
      cachedMaybes = result.cachedMaybes;
    }
    this.setState({
      displaySuggestionIds: filter(cachedSuggestionIds),
      displayMaybeIds: cachedMaybes ? filter(cachedMaybes) : undefined,
    });
  }

  async buildInitialSuggestions(): Promise<{
    cachedSuggestionIds: string[];
    cachedMaybes?: string[];
  }> {
    const {
      personIds,
      personsMap,
      game,
      shift,
      prefilter,
      excludeIds,
      timePeriod,
    } = this.props;

    this.setState({ buildingIds: true });

    // personsMap actually contains some other things that aren't people, such as .all, cast to unknown then back
    let available: string[] | undefined;
    let maybeAvailable: string[] | undefined;
    if (game && shift) {
      try {
        const results = await SearchApi.searchRefereesByShiftAndGame(
          this.props.role,
          shift._id,
          game._id,
          this.props.sessionId || undefined,
        );
        available = results.available;
        maybeAvailable = results.maybe;
      } catch (er) {
        console.log(er);
        this.setState({ buildingIds: false });
        throw er;
      }
    } else {
      console.warn("The GAME and SHIFT weren't available");
      available =
        personIds ||
        shift?.enrolments
          ?.filter(
            (sp) => !this.props.role || Role.hasRole(sp.role, this.props.role),
          )
          ?.map((sp) => sp.person) ||
        (Object.keys(personsMap) as string[]);
    }

    const excludeSet = new Set(this.props.excludeIds || []);

    available = available?.filter((pid) => {
      if (excludeIds && excludeSet.has(pid)) return false;
      if (prefilter && !prefilter(pid)) return false;

      if (timePeriod) {
        console.log("Don't know how to filter by TimePeriod yet");
      }

      if (game && game.persons && pid in game.persons) return false;
      return true;
    });

    maybeAvailable = maybeAvailable?.filter((pid) => {
      if (excludeIds && excludeSet.has(pid)) return false;
      if (prefilter && !prefilter(pid)) return false;

      if (timePeriod) {
        console.log("Don't know how to filter by TimePeriod yet");
      }

      if (game && game.persons && pid in game.persons) return false;
      return true;
    });

    this.setState({
      cachedSuggestionIds: available || null,
      cachedMaybes: maybeAvailable,
      buildingIds: false,
    });
    return { cachedSuggestionIds: available, cachedMaybes: maybeAvailable };
  }
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      { addLoading, setPaysheetVisibilityStatus },
      dispatch,
    ),
  };
}

function mapStateToProps(
  state: IState,
  props: OfficialsSelectorProps,
): Partial<OfficialsSelectorProps> {
  return {
    personsMap: state.persons.map,
    shift: props.shiftId ? state.shifts.map[props.shiftId] : null,
    game: props.gameId ? state.games[props.gameId] : null,
    appointment:
      props.appointment ||
      (state.appointments.map[
        props.appointmentId!
      ] as IAppointment<IGameAppointmentDetail>),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(OfficialsSelectorOld);

// New functional component version
const OfficialsSelector: React.FC<OfficialsSelectorProps> = React.memo((props) => {
  const {
    name,
    role,
    label,
    disabled,
    appointmentId,
    sessionId,
    showHideAppointmentFromSheetButton,
    additionalActions,
    person: directPerson,
    onChange,
    onClear,
    gameId,
    shiftId,
    timePeriod,
    prefilter,
    personIds,
    excludeIds,
    shift,
    game,
    personsMap,
    appointment,
    actions
  } = props;

  // State management with hooks
  const [textValue, setTextValue] = useState<string | React.ReactElement>("");
  const [buildingIds, setBuildingIds] = useState(false);
  const [cachedSuggestionIds, setCachedSuggestionIds] = useState<string[]>([]);
  const [cachedMaybes, setCachedMaybes] = useState<string[]>([]);
  const [displaySuggestionIds, setDisplaySuggestionIds] = useState<string[]>([]);
  const [displayMaybeIds, setDisplayMaybeIds] = useState<string[]>([]);
  const [hasCreatedSuggestionCache, setHasCreatedSuggestionCache] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [isDroppingDown, setIsDroppingDown] = useState(false);
  const [showLoadingView, setShowLoadingView] = useState(false);
  const [showAppointmentDetails, setShowAppointmentDetails] = useState(false);
  const [pendingPersonId, setPendingPersonId] = useState<string | null>(null);

  // Ref to track if component is mounted
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Utility function to get role prefix
  const getPrefix = useCallback((r: Role): string => {
    const def = "an official";
    if (!r) return def;

    switch (r) {
      case Role.referee:
        return "a referee";
      case Role.scoretable:
        return "a scorer";
      case Role.statistician:
        return "a statistician";
      case Role.supervisor:
        return "a supervisor";
      case Role.refereeCoach:
        return "a referee coach";
      case Role.administrative:
        return "an administrator";
      default:
        return def;
    }
  }, []);

  // Get display text for person
  const getDisplayForPersonId = useCallback((
    obj: string | { pid: string; available: boolean },
    formatted: boolean,
  ): string | React.ReactElement => {
    let personId: string;
    let isAvailable: boolean;

    if (typeof obj === "string") {
      personId = obj;
      isAvailable = true;
    } else {
      personId = obj.pid;
      isAvailable = obj.available;
    }

    if (!personId || !personsMap) return "";
    const person = personsMap[personId];
    if (!person) return "";

    let grade: string | React.ReactElement = "";
    if (Role.hasRole(role, Role.referee)) {
      if (formatted) {
        grade = BasketballGrade.componentFromPersonWithDecorations(person) || "";
      } else {
        grade = BasketballGrade.stringFromPersonWithDecorations(person) || "";
      }
    } else if (Role.hasRole(role, Role.refereeCoach)) {
      grade = RefereeCoachGrade.toShortString(person?.refereeCoach?.grade || 0) || "UG";
    } else if (Role.hasRole(role, Role.statistician)) {
      const statGrade = person?.statistician?.grade ?? 0;
      const baseGrade =
        statGrade === 0
          ? "UG"
          : StatisticianGrade.toShortString(statGrade) || "UG";
      grade = person?.statistician?.fibaAccredited
        ? `${baseGrade} • FIBA`
        : baseGrade;
    } else {
      grade = Role.toString(role);
    }

    if (!isAvailable && formatted) {
      return (
        <p style={{ color: "grey" }}>
          {person.fullName} ({grade})
          <br />
          <em>Not available</em>
        </p>
      );
    }

    if (formatted) {
      return (
        <p>
          {person.fullName} ({grade})
        </p>
      );
    }
    return `${person.fullName} (${grade})`;
  }, [personsMap, role]);

  // Initialize text value when appointment or person changes
  useEffect(() => {
    // Priority 1: Use appointment person if available
    if (appointment?.person) {
      const displayValue = getDisplayForPersonId(appointment.person, false);
      setTextValue(displayValue || "");
      setPendingPersonId(null);
    }
    // Priority 2: Use direct person prop if no appointment
    else if (directPerson) {
      const displayValue = getDisplayForPersonId(directPerson, false);
      setTextValue(displayValue || "");
      setPendingPersonId(null);
    }
    // Priority 3: Use pending person if available
    else if (pendingPersonId) {
      const displayValue = getDisplayForPersonId(pendingPersonId, false);
      setTextValue(displayValue || "");
    }
    // Only clear if everything is explicitly null/empty
    else if ((appointment?.person === null || appointment === null) && !directPerson) {
      setTextValue("");
    }
  }, [appointment?.person, directPerson, personsMap, getDisplayForPersonId, pendingPersonId]);

  // Add loading function
  const addLoading = useCallback((loading: BonusType) => {
    if (!appointmentId) return;

    actions
      .addLoading(appointmentId, {
        reason: loading,
        value: BonusType.defaultBonusForReason(
          loading,
          appointment?.payment?.base || 0,
        ),
      })
      .then((resp) => {
        console.log(resp);
      })
      .catch((er) => {
        alert(er.message);
        console.error(er);
      });
  }, [appointmentId, appointment?.payment?.base, actions]);

  // Toggle functions
  const toggleTooltip = useCallback(() => {
    setTooltipOpen(prev => !prev);
  }, []);

  const toggleDropdown = useCallback(() => {
    setIsDroppingDown(prev => !prev);
  }, []);

  const toggleAddLoading = useCallback(() => {
    setShowLoadingView(true);
    setShowAppointmentDetails(true);
  }, []);

  // Filter function for suggestions
  const filterSuggestions = useCallback((initial: string[], query: string) => {
    const searchComponents = query.trim().toLowerCase().split(" ");

    return initial.filter((p) => {
      if (!(p in personsMap)) {
        console.log(`Warning: Person '${p}' was not found in the store`);
        return false;
      }
      const person = personsMap[p];

      return searchComponents.every((q) => {
        const l = q.length;
        const preferredMatch =
          !!person.preferredName &&
          person.preferredName.toLowerCase().slice(0, l) === q;
        const firstNameMatch =
          person.firstName.toLowerCase().slice(0, l) === q;
        const lastNameMatch = person.lastName.toLowerCase().slice(0, l) === q;
        const refereeMatch =
          !!person.referee &&
          !!person.referee.id &&
          String(person.referee.id).slice(0, l) === q;
        return (
          preferredMatch || firstNameMatch || lastNameMatch || refereeMatch
        );
      });
    });
  }, [personsMap]);

  // Build initial suggestions
  const buildInitialSuggestions = useCallback(async (): Promise<{
    cachedSuggestionIds: string[];
    cachedMaybes?: string[];
  }> => {
    setBuildingIds(true);

    try {
      let available: string[] | undefined;
      let maybeAvailable: string[] | undefined;

      if (game && shift) {
        const results = await SearchApi.searchRefereesByShiftAndGame(
          role,
          shift._id,
          game._id,
          sessionId || undefined,
        );
        available = results.available;
        maybeAvailable = results.maybe;
      } else {
        console.warn("The GAME and SHIFT weren't available");
        available =
          personIds ||
          shift?.enrolments
            ?.filter((sp) => !role || Role.hasRole(sp.role, role))
            ?.map((sp) => sp.person) ||
          (Object.keys(personsMap) as string[]);
      }

      const excludeSet = new Set(excludeIds || []);

      available = available?.filter((pid) => {
        if (excludeIds && excludeSet.has(pid)) return false;
        if (prefilter && !prefilter(pid)) return false;

        if (timePeriod) {
          console.log("Don't know how to filter by TimePeriod yet");
        }

        if (game && game.persons && pid in game.persons) return false;
        return true;
      });

      maybeAvailable = maybeAvailable?.filter((pid) => {
        if (excludeIds && excludeSet.has(pid)) return false;
        if (prefilter && !prefilter(pid)) return false;

        if (timePeriod) {
          console.log("Don't know how to filter by TimePeriod yet");
        }

        if (game && game.persons && pid in game.persons) return false;
        return true;
      });

      if (isMountedRef.current) {
        setCachedSuggestionIds(available || []);
        setCachedMaybes(maybeAvailable || []);
        setBuildingIds(false);
      }

      return { cachedSuggestionIds: available || [], cachedMaybes: maybeAvailable };
    } catch (er) {
      console.log(er);
      if (isMountedRef.current) {
        setBuildingIds(false);
      }
      throw er;
    }
  }, [
    game,
    shift,
    role,
    personIds,
    excludeIds,
    prefilter,
    timePeriod,
    personsMap,
  ]);

  // Build suggestions for autosuggest
  const buildSuggestions = useCallback(async (query: string) => {
    // Only update textValue if we don't have a selected person
    if (!appointment?.person && !directPerson) {
      setTextValue(query);
    }

    if (buildingIds) return null;

    let suggestionIds = cachedSuggestionIds;
    let maybes = cachedMaybes;

    if (!suggestionIds || suggestionIds.length === 0) {
      const result = await buildInitialSuggestions();
      suggestionIds = result.cachedSuggestionIds;
      maybes = result.cachedMaybes || [];
    }

    if (isMountedRef.current) {
      setDisplaySuggestionIds(filterSuggestions(suggestionIds, query));
      setDisplayMaybeIds(maybes ? filterSuggestions(maybes, query) : []);
    }
  }, [
    appointment?.person,
    directPerson,
    buildingIds,
    cachedSuggestionIds,
    cachedMaybes,
    buildInitialSuggestions,
    filterSuggestions,
  ]);

  // Roster official function
  const rosterOfficial = useCallback((pid: string) => {
    let value: any | null = null;

    if (role === Role.referee) {
      const hasApprentice = appointment && (appointment.details as IGameAppointmentDetail).apprentice;
      value = onChange(name, pid, hasApprentice || false);
    } else {
      value = onChange(name, pid);
    }

    if (value && Boolean(value && typeof value.then === "function")) {
      value.catch(() => setTextValue(""));
    }
  }, [role, appointment, onChange, name]);

  // Handle suggestion selection
  const onSuggestionSelected = useCallback((
    event: any,
    { suggestion }: { suggestion: { pid: string; available: boolean } },
  ) => {
    const { pid, available } = suggestion;

    // Set pending person immediately to preserve the selection
    setPendingPersonId(pid);

    if (!available) {
      const personName = personsMap[pid]?.fullName || pid;
      confirm({
        title: `${personName} is not available`,
        message: "Do you want to allocate them to the game anyway?",
        confirmText: "Yes",
      }).then((confirmed) => {
        if (confirmed) {
          rosterOfficial(pid);
        } else {
          // User cancelled, clear pending
          setPendingPersonId(null);
        }
      });
    } else {
      rosterOfficial(pid);
    }
  }, [personsMap, rosterOfficial]);

  // Handle text change
  const onTextValueChange = useCallback((e: any, { newValue }: { newValue: string }) => {
    setTextValue(newValue);

    // If user is typing something different, clear pending selection
    if (pendingPersonId) {
      const pendingDisplayValue = getDisplayForPersonId(pendingPersonId, false);
      if (newValue !== pendingDisplayValue) {
        setPendingPersonId(null);
      }
    }
  }, [pendingPersonId, getDisplayForPersonId]);

  // Clear suggestions
  const onSuggestionsClearRequested = useCallback(() => {
    setDisplaySuggestionIds([]);
    setDisplayMaybeIds([]);
  }, []);

  // Get suggestion value for autosuggest
  const getSuggestionValue = useCallback((suggestion: { pid: string; available: boolean }) => {
    const result = getDisplayForPersonId(suggestion, false);
    return result as string;
  }, [getDisplayForPersonId]);

  // Render suggestion
  const renderSuggestion = useCallback((person: { pid: string; available: boolean }) => {
    return getDisplayForPersonId(person, true);
  }, [getDisplayForPersonId]);

  // Render input component
  const renderInput = useCallback(({ disabled, onBlur, ...other }: any) => {
    const { styles, key, ...otherNoKey } = other;
    return (
      <>
        <TextInput
          disabled={disabled}
          onBlur={(e: any) => {
            if (onBlur) onBlur(e);
          }}
          {...otherNoKey}
        />
        {buildingIds && (
          <div>
            <span
              className="loading"
              style={{
                position: "absolute",
                top: ".2rem",
                right: "0.1rem",
                zIndex: 2,
                display: "block",
                textAlign: "center",
                pointerEvents: "none",
              }}
            />
            <div
              style={{ backgroundColor: styles?.backgroundColor || "#FFF" }}
            />
          </div>
        )}
      </>
    );
  }, [buildingIds]);

  // Render suggestions container
  const renderSuggestionsContainer = useCallback((options: any) => {
    const { containerProps, children } = options;
    const { key, ...containerPropsNoKey } = containerProps;

    return (
      <div {...containerPropsNoKey}>
        <div
          style={{
            zIndex: 999,
            overflow: "visible",
            position: "absolute",
            backgroundColor: "var(--color-bg-card)",
            border: "1px solid var(--color-divider)",
            borderRadius: "4px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
            color: "var(--color-text-primary)"
          }}
        >
          {children}
          {!children && (
            <p>
              <em>There are no results</em>
            </p>
          )}
        </div>
      </div>
    );
  }, []);

  // Render the component
  const person = appointment?.person;
  const personObj = person && personsMap && personsMap[person];

  if (!role) return <p>Loading ...</p>;

  let backgroundColor = "";
  const appointmentStatus = appointment && appointment.status;

  if (!person) backgroundColor = SSColors.emptyOfficial;
  else if (!appointment) {
    if (appointmentId) backgroundColor = "red";
  } else if (appointmentStatus === AppointmentStatus.rejected)
    backgroundColor = SSColors.rejectedAppointment;
  else if ((appointment.details as IGameAppointmentDetail).apprentice)
    backgroundColor = SSColors.apprentice;

  const checkSpanId = `confirmStatus_${appointmentId}`;
  let statusObj: React.ReactElement | null = null;
  let statusText: string | null = null;

  // show tick
  if (appointment && appointmentStatus) {
    if (AppointmentStatus.shouldShowTick(appointmentStatus)) {
      const confirmStatus = appointment.statuses.filter(
        (s) => s.status === AppointmentStatus.confirmed,
      );
      const person = confirmStatus[0]?.person;
      const personName = personsMap && person && personsMap[person] ? (personsMap[person].preferredName || personsMap[person].firstName) : "Someone";
      let reason = ''
      if (!!confirmStatus[0]?.comment) {
        reason = ` Reason: '${confirmStatus[0].comment}'`;
      }

      statusObj = <Checkmark id={checkSpanId} />;
      statusText = `${personName} confirmed this appointment on ${confirmStatus && toDateTime(confirmStatus[0].date).toLocaleString(DateTime.DATETIME_FULL)
        }${reason}`;
    } else if (appointmentStatus === AppointmentStatus.rejected) {
      const rejectStatus = appointment.statuses.filter(
        (s) => s.status === AppointmentStatus.rejected,
      );
      statusObj = (
        <span id={checkSpanId} style={{ color: "red" }}>
          {" "}
          &#10005;
        </span>
      );
      statusText = `${personObj && (personObj.preferredName || personObj.firstName)
        } rejected their appointment with the reason: '${rejectStatus[0].comment
        }'`;
    }
  }

  const hasApprentice = (appointment && (appointment.details as IGameAppointmentDetail).apprentice) || false;

  const isDisabled =
    disabled ||
    (appointment && !AppointmentStatus.canRemoveOrChange(appointment.status)) ||
    false;

  const additionalSideActions: {
    title: string;
    action: Function;
    color?: string;
    disabled?: boolean;
  }[][] = [];

  if (person) {
    additionalSideActions.push([
      {
        title: `Remove ${(personObj && (personObj.preferredName || personObj.firstName)) || label}`,
        action: () => onClear?.(name),
        color: "var(--color-attention-red)",
      },
      {
        title: `Withdrawal ${(personObj && (personObj.preferredName || personObj.firstName)) || label} (with replacement)`,
        action: () => onClear?.(name, "Withdrawal, with replacement"),
        color: "var(--color-attention-red)",
      },
      {
        title: `Withdrawal ${(personObj && (personObj.preferredName || personObj.firstName)) || label} (no replacement)`,
        action: () => onClear?.(name, "Withdrawal, no replacement"),
        color: "var(--color-attention-red)",
      },
    ]);

    const secondSet: { title: string; action: Function }[] = [];
    const thirdSet: { title: string; action: Function }[] = [];

    if (Role.hasRole(role, Role.referee)) {
      secondSet.push({
        title: `${hasApprentice ? "Remove" : "Add"} apprentice`,
        action: () => onChange(name, person, !hasApprentice),
      });
    }

    thirdSet.push({
      title: "Add loading",
      action: () => toggleAddLoading(),
    });

    // show these bonus types on thirdSet
    const bonusTypes = [BonusType.solo, BonusType.venueCaptain];
    for (const bonusType of bonusTypes) {
      const hasBonusType = appointment?.payment?.bonuses?.some(
        (b) => b.reason === bonusType,
      );
      if (!hasBonusType) {
        const defaultBonusValue = BonusType.defaultBonusForReason(
          bonusType,
          appointment?.payment?.base || 0,
        );
        thirdSet.push({
          title: `Add ${BonusType.toLongReason(
            bonusType,
          ).toLowerCase()} loading ($${defaultBonusValue})`,
          action: () => addLoading(bonusType),
        });
      }
    }

    if (appointment && showHideAppointmentFromSheetButton) {
      const shouldShow = appointment?.payment?.showTotal === false;
      const hideShow = shouldShow ? "Show" : "Hide";
      secondSet.push({
        title: `${hideShow} appointment total on paysheet`,
        action: () =>
          actions.setPaysheetVisibilityStatus(appointment._id, shouldShow),
      });
    }

    additionalSideActions.push(secondSet);
    additionalSideActions.push(thirdSet);
  }

  if (additionalActions) {
    additionalSideActions.push(additionalActions);
  }

  const rightPadding = additionalSideActions.length > 0 ? 25 : 0;
  const hideAppointmentModal = () => setShowAppointmentDetails(false);

  const suggestions = [
    ...displaySuggestionIds.map((pid) => ({
      pid,
      available: true,
    })),
    ...displayMaybeIds.map((pid) => ({
      pid,
      available: false,
    })),
  ];

  return (
    <div style={{ whiteSpace: "nowrap" }}>
      <div
        style={{
          display: "inline-block",
          width: `calc(100% - ${rightPadding}px)`,
        }}
      >
        <Autosuggest
          id={name}
          renderInputComponent={renderInput}
          suggestions={suggestions}
          onSuggestionsFetchRequested={(event) => buildSuggestions(event.value)}
          onSuggestionsClearRequested={onSuggestionsClearRequested}
          onSuggestionSelected={onSuggestionSelected}
          renderSuggestionsContainer={renderSuggestionsContainer}
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={renderSuggestion}
          focusInputOnSuggestionClick={false}
          shouldRenderSuggestions={() => true}
          inputProps={{
            placeholder: `Type ${getPrefix(role)}'s name...`,
            value: textValue || "",
            onChange: onTextValueChange,
            disabled: isDisabled,
            style: {
              backgroundColor,
              border: "none",
              outline: "none",
              padding: "0px 5px 0px 10px",
              borderBottom: "1px solid var(--mantine-color-gray-4)",
              borderRadius: "1px",
              width: "100%",
              boxShadow: "none",
              borderWidth: "0px",
            },
          }}
          disabled={isDisabled}
        />
        {label && (
          <Text size="sm" fw={500} style={{ color: "gray", fontSize: "14px" }}>
            <em>
              {label} {hasApprentice ? "(+Ap)" : ""}
            </em>{" "}
            {statusText && statusObj ? (
              <Tooltip
                label={statusText}
                opened={tooltipOpen}
                position="right"
              >
                <span id={checkSpanId} onClick={toggleTooltip} style={{ cursor: "pointer" }}>
                  {statusObj}
                </span>
              </Tooltip>
            ) : (statusObj || "")}
          </Text>
        )}
      </div>
      {additionalSideActions.length > 0 && (
        <div style={{ display: "inline-block", verticalAlign: "top" }}>
          <Menu
            opened={isDroppingDown}
            onClose={() => setIsDroppingDown(false)}
            onOpen={() => setIsDroppingDown(true)}
          >
            <Menu.Target>
              <Button
                variant="light"
                color="gray"
                size="sm"
                style={{ padding: "5px 4px" }}
                onClick={toggleDropdown}
              >
                <FontAwesomeIcon icon={faEllipsisV} />
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {additionalSideActions.map((actions, idx) => (
                <div key={idx}>
                  {idx !== 0 && <Menu.Divider />}
                  {actions.map((action, actionIdx) => (
                    <Menu.Item
                      disabled={action.disabled}
                      onClick={(e) => action.action(e)}
                      style={{ color: action.color, height: "5vh" }}
                      key={actionIdx}
                    >
                      {action.title}
                    </Menu.Item>
                  ))}
                </div>
              ))}
              <Menu.Divider />
              <Menu.Item
                style={{ height: "5vh" }}
                onClick={() => setShowAppointmentDetails(true)}
              >
                Show details
              </Menu.Item>
              {appointment?.payment && (
                <Menu.Label>
                  Total: ${Number(appointment.payment.total).toFixed(2)}
                </Menu.Label>
              )}
            </Menu.Dropdown>
          </Menu>
        </div>
      )}

      {showAppointmentDetails && (
        <Modal
          opened={showAppointmentDetails}
          onClose={hideAppointmentModal}
          title="Appointment"
        >
          <AppointmentView
            appointmentId={appointmentId!}
            defaultTab={showLoadingView ? "Bonuses" : undefined}
            sessionId={sessionId}
            disableEdits={false}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
            <Button
              variant="outline"
              color="gray"
              onClick={hideAppointmentModal}
            >
              Close
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
});

// Connect to Redux using hooks
const OfficialsSelectorConnected: React.FC<Omit<OfficialsSelectorProps, 'personsMap' | 'shift' | 'game' | 'appointment' | 'actions'>> = (props) => {
  const dispatch = useDispatch();
  const personsMap = useSelector((state: IState) => state.persons.map);
  const shift = useSelector((state: IState) =>
    props.shiftId ? state.shifts.map[props.shiftId] : null
  );
  const game = useSelector((state: IState) =>
    props.gameId ? state.games[props.gameId] : null
  );
  const appointment = useSelector((state: IState) =>
    props.appointmentId ?
      state.appointments.map[props.appointmentId] as IAppointment<IGameAppointmentDetail> :
      null
  );

  const actions = useMemo(() => ({
    addLoading: (appointmentId: string, loading: IOfficialLoading) =>
      dispatch(addLoading(appointmentId, loading) as any),
    setPaysheetVisibilityStatus: (appointmentId: string, shouldShow: boolean) =>
      dispatch(setPaysheetVisibilityStatus(appointmentId, shouldShow) as any),
  }), [dispatch]);

  return (
    <OfficialsSelector
      {...props}
      personsMap={personsMap}
      shift={shift}
      game={game}
      appointment={appointment}
      actions={actions}
    />
  );
};

export { OfficialsSelectorConnected as OfficialsSelector };
